// client/src/services/messageHandlers.js

// Priority: Tekion → CRM (Vinsolutions) → Twilio
export function pickCallerName(data, fallback) {
  if (!data) return fallback;

  const td = data.tekionData;
  if (td && td.customerId) {
    if (td.firstName || td.lastName) {
      return `${td.firstName || ''} ${td.lastName || ''}`.trim();
    }
    if (td.displayName) return td.displayName;
    if (td.name) return td.name;
    if (td.customerName) return td.customerName;
  }

  if (data.contact?.fullName) return data.contact.fullName;

  if (data.hasTwilioData && data.twilioData?.callerName) {
    return data.twilioData.callerName;
  }

  return fallback;
}

export const messageHandlers = {

  handleProgressiveUpdate(message, setCurrentCalls, currentCallIdRef, audioRef) {
    console.log('🔄 Processing progressive update, stage:', message.stage, 'for call:', message.callInfo?.callId);
    const callId = message.callInfo?.callId;

    if (!callId) {
      console.warn('⚠️ No callId in progressive update');
      return { callId: null, stage: null };
    }

    setCurrentCalls(prevCalls => {
      const existingCallIndex = prevCalls.findIndex(call => call.callId === callId);

      if (existingCallIndex !== -1) {
        console.log(`🔄 Updating existing call ${callId} at stage ${message.stage}`);
        const updatedCalls = [...prevCalls];
        const existingCall = { ...updatedCalls[existingCallIndex] };

        // ✅ FIX: Don't allow lower stages to overwrite higher stages
        if (existingCall.loadingStage >= 5 && message.stage < 5) {
          console.log(`⏭️ Skipping stage ${message.stage} update - call already at stage ${existingCall.loadingStage}`);
          return prevCalls; // Don't update if we're already at a higher stage
        }

        if (message.stage === 1) {
          existingCall.phoneNumber = message.data.phoneNumber;
          existingCall.callerNumber = message.callInfo.callerNumber;
        } else if (message.stage === 2) {
          existingCall.customerData = {
            ...existingCall.customerData,
            ...message.data
          };
          if (message.data.contact?.fullName) {
            existingCall.callerName = message.data.contact.fullName;
          }
        } else if (message.stage === 3) {
          existingCall.customerData = {
            ...existingCall.customerData,
            ...message.data
          };
        } else if (message.stage === 4) {
          // ✅ FIX: Preserve Twilio data when setting stage 4
          existingCall.customerData = {
            ...message.data,
            // Preserve Twilio if it was already set
            twilioData: existingCall.customerData?.twilioData || message.data?.twilioData,
            hasTwilioData: existingCall.customerData?.hasTwilioData || message.data?.hasTwilioData
          };
          existingCall.vinSolutionsLoaded = true;

          // Priority: Tekion → CRM (Vinsolutions) → Twilio
          existingCall.callerName = pickCallerName(existingCall.customerData, existingCall.callerName);

          console.log('✅ Customer data loaded for call', callId, '→ name:', existingCall.callerName);
        }

        existingCall.lastUpdate = new Date().toISOString();

        // ✅ FIX: Only update loadingStage if it's higher than current
        if (message.stage > existingCall.loadingStage) {
          existingCall.loadingStage = message.stage;
        }

        updatedCalls[existingCallIndex] = existingCall;

        return updatedCalls;
      } else {
        if (message.stage === 1) {
          console.log('🆕 Creating new call entry for:', callId);
          const newCall = {
            id: Date.now(),
            callId: callId,
            callerName: message.callInfo.callerName || message.callInfo.callerNumber,
            callerNumber: message.callInfo.callerNumber,
            extension: message.callInfo.extension,
            status: message.callInfo.status,
            timestamp: message.callInfo.timestamp || new Date().toISOString(),
            ringStartTime: Date.now(),
            phoneNumber: message.data.phoneNumber,
            customerData: null,
            fullyLoaded: false,
            vinSolutionsLoaded: false,
            tekionLoaded: false,
            loadingStage: 1,
            lastUpdate: new Date().toISOString(),
            timerStarted: false,
            callEnded: false,
            ringDuration: 0,
            isUnanswered: false
          };

          return [newCall, ...prevCalls];
        } else {
          console.warn(`⚠️ Received stage ${message.stage} for unknown call ${callId} - ignoring`);
          return prevCalls;
        }
      }
    });

    if (message.stage === 1) {
     //currentCallIdRef.current = callId;
      audioRef.current?.play().catch(() => console.log('🔇 Audio play prevented'));
    }

    return { callId, stage: message.stage, data: message.data };
  },

  handleMultiCallUpdate(message, setCurrentCalls) {
    console.log('🔄 Processing multi-call update:', message);

    const currentCallId = message.callInfo?.callId;

    if (!currentCallId) {
      console.warn('⚠️ Invalid multi-call update - no callId');
      return null;
    }

    console.log(`📊 Multi-call update for call ${currentCallId} at stage ${message.stage}`);
    console.log(`📊 Other calls: ${message.multiCall?.otherCallsData?.length || 0}`);

    setCurrentCalls(prevCalls => {
      const updatedCalls = [...prevCalls];
      let currentCallIndex = updatedCalls.findIndex(call => call.callId === currentCallId);

      if (currentCallIndex !== -1) {
        console.log(`🔄 Updating current call ${currentCallId} (stage ${message.stage})`);
        updatedCalls[currentCallIndex] = {
          ...updatedCalls[currentCallIndex],
          customerData: message.data || updatedCalls[currentCallIndex].customerData,
          loadingStage: message.stage || updatedCalls[currentCallIndex].loadingStage,
          vinSolutionsLoaded: message.stage >= 4 ? true : updatedCalls[currentCallIndex].vinSolutionsLoaded,
          fullyLoaded: false,
          lastUpdate: new Date().toISOString()
        };

        // Priority: Tekion → CRM (Vinsolutions) → Twilio
        updatedCalls[currentCallIndex].callerName = pickCallerName(
          message.data,
          updatedCalls[currentCallIndex].callerName
        );
      } else {
        console.log(`🆕 Creating current call ${currentCallId} from multi-call update (stage ${message.stage})`);

        // Priority: Tekion → CRM (Vinsolutions) → Twilio
        const callerName = pickCallerName(
          message.data,
          message.callInfo.callerName || message.callInfo.callerNumber
        );

        const newCall = {
          id: Date.now(),
          callId: currentCallId,
          callerName: callerName,
          callerNumber: message.callInfo.callerNumber,
          extension: message.callInfo.extension,
          status: message.callInfo.status,
          timestamp: message.callInfo.timestamp || message.timestamp || new Date().toISOString(),
          phoneNumber: message.callInfo.phoneNumber,
          customerData: message.data || null,
          fullyLoaded: false,
          vinSolutionsLoaded: message.stage >= 4,
          tekionLoaded: false,
          loadingStage: message.stage || 1,
          lastUpdate: new Date().toISOString(),
          timerStarted: false,
          callEnded: false
        };

        updatedCalls.unshift(newCall);
      }

      if (message.multiCall?.otherCallsData && Array.isArray(message.multiCall.otherCallsData)) {
        message.multiCall.otherCallsData.forEach(otherCall => {
          const otherCallId = otherCall.callId;
          let otherCallIndex = updatedCalls.findIndex(call => call.callId === otherCallId);

          if (otherCallIndex !== -1) {
            console.log(`🔄 Updating other call ${otherCallId} (stage ${otherCall.stage})`);
            updatedCalls[otherCallIndex] = {
              ...updatedCalls[otherCallIndex],
              customerData: otherCall.data || updatedCalls[otherCallIndex].customerData,
              loadingStage: otherCall.stage || updatedCalls[otherCallIndex].loadingStage,
              vinSolutionsLoaded: otherCall.stage >= 4 ? true : updatedCalls[otherCallIndex].vinSolutionsLoaded,
              fullyLoaded: false,
              lastUpdate: new Date().toISOString()
            };

            // Priority: Tekion → CRM (Vinsolutions) → Twilio
            updatedCalls[otherCallIndex].callerName = pickCallerName(
              otherCall.data,
              updatedCalls[otherCallIndex].callerName
            );
          } else {
            console.log(`🆕 Creating other call ${otherCallId} from multi-call update (stage ${otherCall.stage})`);

            const callInfo = message.multiCall.allCalls?.find(c => c.callId === otherCallId);

            // Priority: Tekion → CRM (Vinsolutions) → Twilio
            const callerName = pickCallerName(otherCall.data, otherCall.phoneNumber || 'Unknown');

            const newOtherCall = {
              id: Date.now() + Math.random(),
              callId: otherCallId,
              callerName: callerName,
              callerNumber: otherCall.phoneNumber || callInfo?.phoneNumber || 'Unknown',
              extension: message.callInfo.extension,
              status: 'Ringing',
              timestamp: message.timestamp || new Date().toISOString(),
              phoneNumber: otherCall.phoneNumber || callInfo?.phoneNumber,
              customerData: otherCall.data || null,
              fullyLoaded: false,
              vinSolutionsLoaded: otherCall.stage >= 4,
              tekionLoaded: false,
              loadingStage: otherCall.stage || 1,
              lastUpdate: new Date().toISOString(),
              timerStarted: false,
              callEnded: false
            };

            updatedCalls.unshift(newOtherCall);
          }
        });
      }

      console.log(`✅ Multi-call update complete - processed ${message.multiCall?.totalCalls || 1} calls`);
      console.log(`📊 Total calls in state: ${updatedCalls.length}`);

      return updatedCalls;
    });

    return { currentCallId, stage: message.stage, data: message.data, multiCall: message.multiCall };
  },

  handleCallNotification(message, currentUser, setCurrentCalls, currentCallIdRef, audioRef) {
    if (message.data.userExtension !== currentUser.username) {
      return null;
    }

    console.log('📞 Processing legacy call notification');

    const callId = message.data?.callId;
    if (!callId) {
      console.warn('⚠️ No callId in call notification');
      return null;
    }

    setCurrentCalls(prevCalls => {
      const existingCall = prevCalls.find(call => call.callId === callId);

      if (existingCall) {
        console.log(`⏭️ Skipping legacy notification - call ${callId} already exists`);
        return prevCalls;
      }

      console.log('🆕 Creating call from legacy notification:', callId);

      // Priority: Tekion → CRM (Vinsolutions) → Twilio
      const callerName = pickCallerName(message.data.customerData, message.data.callerName);

      const newCall = {
        id: Date.now(),
        callId: callId,
        callerName: callerName,
        callerNumber: message.data.callerNumber,
        extension: message.data.extension,
        status: message.data.status,
        timestamp: message.data.timestamp || new Date().toISOString(),
        ringStartTime: Date.now(),
        customerData: message.data.customerData,
        fullyLoaded: true,
        vinSolutionsLoaded: true,
        tekionLoaded: false,
        loadingStage: 4,
        lastUpdate: new Date().toISOString(),
        timerStarted: false,
        callEnded: false,
        ringDuration: 0,
        isUnanswered: false
      };

      return [newCall, ...prevCalls];
    });
   //currentCallIdRef.current = callId;
    audioRef.current?.play().catch(() => console.log('🔇 Audio play prevented'));

    return callId;
  },
};